import { BoxProps, Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import { FormikHelpers, useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FeatureFlagged } from "@/Components/FeatureFlagging";
import { SendIcon } from "@/Components/Icons/SendIcon";
import { WarcButton } from "@/Components/v2/atoms/Button";
import { BrainIcon } from "@/Components/v2/atoms/Icon/BrainIcon";

import { DocumentUpload } from "./DocumentUpload";
import { WarcTooltip } from "./WarcTooltip";

type Form = {
  question: string;
};

type AskQuestionInputProps = {
  onAskQuestion: (question: string, warcEnabled: boolean) => void;
  isAnswerPending: boolean;
  warcEnabledForTier: boolean;
  prefillQuestion?: string;
} & BoxProps;

export const AskQuestionInput: React.FC<AskQuestionInputProps> = ({
  onAskQuestion,
  isAnswerPending,
  warcEnabledForTier,
  prefillQuestion,
  ...props
}) => {
  const [warcEnabled, setWarcEnabled] = useState<boolean>(warcEnabledForTier);
  const { palette } = useTheme();
  const initialFormValues: Form = {
    question: prefillQuestion || "",
  };

  const handleSubmit = useCallback(
    (values: Form, { resetForm }: FormikHelpers<Form>) => {
      const shouldUseWarc = warcEnabledForTier && warcEnabled;
      onAskQuestion(values.question, shouldUseWarc);
      resetForm();
    },
    [onAskQuestion, warcEnabled, warcEnabledForTier]
  );

  const validate = useCallback((values: Form) => {
    if (!values.question?.trim() || values.question.length < 3) {
      return {
        question: "Requires at least 3 characters",
      };
    }
    return {};
  }, []);

  const formik = useFormik<Form>({
    initialValues: initialFormValues,
    onSubmit: handleSubmit,
    validate,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    isInitialValid: false,
  });

  const isSubmitDisabled = useMemo(
    () => Boolean(formik.errors.question) || isAnswerPending,
    [formik.errors.question, isAnswerPending]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        if (event.shiftKey) {
          return;
        }

        event.preventDefault();
        if (!isSubmitDisabled) {
          formik.handleSubmit();
        }
      }
    },
    [isSubmitDisabled, formik]
  );

  const handleAgentToggle = useCallback(() => {
    setWarcEnabled((prev) => !prev);
  }, []);

  // Effect to handle when feature flag or tier changes and initial mount
  useEffect(() => {
    if (warcEnabledForTier) {
      // Only set to true if the feature is enabled
      setWarcEnabled(true);
    } else {
      setWarcEnabled(false);
    }
  }, [warcEnabledForTier]);

  const tooltipMessage = useMemo(() => {
    if (!warcEnabledForTier) {
      return "WARC References are a premium feature. Contact us for more information.";
    }
    if (isAnswerPending) {
      return "MAITE may use WARC to answer your question";
    }

    return warcEnabled
      ? "WARC is enabled. MAITE may use WARC to answer your question."
      : "WARC is disabled. Questions will be grounded in your MMM data and general knowledge.";
  }, [warcEnabledForTier, warcEnabled, isAnswerPending]);

  return (
    <Stack
      component="div"
      direction="row"
      position="relative"
      mt="auto"
      {...props}
      borderRadius={1.5}
      bgcolor={palette.action.hover}
      p={1.5}
      alignItems="center"
      spacing={1}
    >
      <Stack component="form" onSubmit={formik.handleSubmit} direction="column" sx={{ flex: 1 }}>
        <TextField
          multiline
          rows={2}
          {...formik.getFieldProps("question")}
          fullWidth
          placeholder="Ask a question about your GrowthOS data"
          variant="standard"
          onKeyDown={handleKeyDown}
          sx={{ mb: 1 }}
          slotProps={{
            input: {
              disableUnderline: true,
            },
          }}
        />

        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="flex-start"
          sx={{ minHeight: "40px" }}
        >
          <FeatureFlagged featureName="growthosHendrenDocumentsInput">
            <Stack direction="row" alignItems="center" height="100%">
              <DocumentUpload hasPendingAction={isAnswerPending} />
            </Stack>
          </FeatureFlagged>

          <FeatureFlagged featureName="enableWarc">
            <WarcTooltip message={tooltipMessage} placement="top">
              <WarcButton
                onClick={warcEnabledForTier ? handleAgentToggle : undefined}
                isPending={isAnswerPending}
                isFeatureEnabled={warcEnabledForTier}
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    gap: "6px",
                    px: "10px",
                    py: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  <BrainIcon
                    status={isAnswerPending ? "pending" : warcEnabled ? "enabled" : "disabled"}
                    disabled={!warcEnabledForTier}
                    sx={{
                      width: "20px",
                      height: "20px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: 1,
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    WARC
                  </Typography>
                </Stack>
              </WarcButton>
            </WarcTooltip>
          </FeatureFlagged>
          <Stack direction="row" alignItems="center" ml="auto" height="100%">
            <Button
              type="submit"
              disabled={isSubmitDisabled}
              sx={{
                minHeight: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SendIcon opacity={isSubmitDisabled ? 0.5 : 1} />
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
